import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { fetchItems, fetchLocations, fetchCompartments, fetchInventory, addInventory } from '../api/api';
import Select from 'react-select';
import { Form, Button } from 'react-bootstrap';

// Utility to normalize strings: remove punctuation, diacritics, lowercase, trim
function normalize(str) {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/[^\w\s]/gi, '')        // Remove punctuation
    .replace(/\s+/g, ' ')            // Collapse whitespace
    .toLowerCase()
    .trim();
}

// Custom filter for react-select
const softFilterOption = (option, inputValue) => {
  if (!inputValue) return true;
  const normLabel = normalize(option.label);
  const normInput = normalize(inputValue);
  return normLabel.includes(normInput);
};

const AddProductForm = (props) => {
  const [formData, setFormData] = useState({
    squad_id: '',
    product_id: '',
    compartment_id: '',
    nsn_size_id: '',
    quantity: '1'
  });
  const [items, setItems] = useState([]);
  const [sqadrons, setLocations] = useState([]);
  const [compartments, setCompartments] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [nsns, setItemNsns] = useState([]);

  useEffect(() => {
    fetchItems()
      .then(data => setItems(data))
      .catch(error => console.error('Error fetching items:', error));
    fetchLocations()
      .then(data => setLocations(data))
      .catch(error => console.error('Error fetching locations:', error));
  }, []);

  useEffect(() => {
    if (formData.squad_id) {
      fetchCompartments(formData.squad_id)
        .then(data => setCompartments(data))
        .catch(error => console.error('Error fetching compartments:', error));
    }
  }, [formData.squad_id]);

    useEffect(() => {
    if (formData.product_id) {
      fetchItems(formData.product_id)
        .then(data => setItemNsns(data.nsn_info))
        .catch(error => console.error('Error fetching compartments:', error));
    }
  }, [formData.product_id]);

  // Find the selected item object for size info
  useEffect(() => {
    if (formData.product_id) {
      const found = items.find(item => String(item.id) === String(formData.product_id));
      setSelectedItem(found);
      // Reset nsn_size_id if switching items
      setFormData(fd => ({
        ...fd,
        nsn_size_id: ''
      }));
    } else {
      setSelectedItem(null);
      setFormData(fd => ({
        ...fd,
        nsn_size_id: ''
      }));
    }
  }, [formData.product_id, items]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Prepare payload as per new API
    const payload = {
      squad_id: formData.squad_id || '',
      product_id: formData.product_id ? Number(formData.product_id) : 0,
      compartment_id: formData.compartment_id ? Number(formData.compartment_id) : 0,
      nsn_size_id: formData.nsn_size_id ? Number(formData.nsn_size_id) : null,
      quantity: formData.quantity ? Number(formData.quantity) : 1
    };

    addInventory([payload])
      .then(() => {
        toast.success('Product added successfully!');
        fetchInventory()
          .then(products => {
            props.setProducts(products);
          })
          .catch(error => {
            console.error('Error fetching products:', error);
          });
        // Only reset the quantity and nsn_size_id, keep other fields as-is
        setFormData(fd => ({
          ...fd,
          quantity: '1',
          nsn_size_id: ''
        }));
        if (!props.addAnother && props.onSuccess) props.onSuccess();
      })
      .catch(err => {
        toast.error('Failed to add product: ' + (err.message || ''));
      });
  };

  // Prepare options for react-select
  const squadronOptions = sqadrons.map(squadron => ({
    value: squadron.id,
    label: squadron.name
  }));

  const compartmentOptions = compartments.map(compartment => ({
    value: compartment.id,
    label: compartment.name
  }));

  const itemOptions = items.map(item => ({
    value: item.id,
    label: item.name
  }));

  // NSN size options if available
  const nsnSizeOptions = selectedItem && nsns && nsns.length > 0
    ? nsns.map(nsn => ({
        value: nsn.id,
        label: `${nsn.size} (${nsn.nsn})`
      }))
    : [];

  // Handlers for react-select
  const handleSquadronChange = (selected) => {
    setFormData({
      ...formData,
      squad_id: selected ? selected.value : ''
    });
    setFormData(fd => ({
      ...fd,
      compartment_id: ''
    }));
  };

  const handleCompartmentChange = (selected) => {
    setFormData({
      ...formData,
      compartment_id: selected ? selected.value : ''
    });
  };

  const handleItemChange = (selected) => {
    setFormData({
      ...formData,
      product_id: selected ? selected.value : ''
    });
  };

  const handleNsnSizeChange = (selected) => {
    setFormData({
      ...formData,
      nsn_size_id: selected ? selected.value : ''
    });
  };

  return (
    <Form onSubmit={handleSubmit} id="add-product-form">
      {/* Vertical layout: one field per row */}
      <Form.Group className="mb-3">
        <Form.Label>Squadron</Form.Label>
        <Select
          inputId="squad_id"
          name="squad_id"
          value={squadronOptions.find(opt => String(opt.value) === String(formData.squad_id)) || null}
          onChange={handleSquadronChange}
          options={squadronOptions}
          placeholder="Select a squadron"
          isClearable
          classNamePrefix="react-select"
          filterOption={softFilterOption}
        />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Compartment</Form.Label>
        <Select
          inputId="compartment_id"
          name="compartment_id"
          value={compartmentOptions.find(opt => String(opt.value) === String(formData.compartment_id)) || null}
          onChange={handleCompartmentChange}
          options={compartmentOptions}
          placeholder="Select a compartment"
          isClearable
          classNamePrefix="react-select"
          filterOption={softFilterOption}
        />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Item</Form.Label>
        <Select
          inputId="product_id"
          name="product_id"
          value={itemOptions.find(opt => String(opt.value) === String(formData.product_id)) || null}
          onChange={handleItemChange}
          options={itemOptions}
          placeholder="Select an item"
          isClearable
          classNamePrefix="react-select"
          filterOption={softFilterOption}
        />
      </Form.Group>
      {/* Show NSN size dropdown if available */}
      {selectedItem && nsnSizeOptions.length > 0 && (
        <Form.Group className="mb-3">
          <Form.Label>Size</Form.Label>
          <Select
            inputId="nsn_size_id"
            name="nsn_size_id"
            value={nsnSizeOptions.find(opt => String(opt.value) === String(formData.nsn_size_id)) || null}
            onChange={handleNsnSizeChange}
            options={nsnSizeOptions}
            placeholder="Select a size"
            isClearable
            classNamePrefix="react-select"
            required
            filterOption={softFilterOption}
          />
        </Form.Group>
      )}
      <Form.Group className="mb-3">
        <Form.Label>Quantity</Form.Label>
        <Form.Control
          type="number"
          name="quantity"
          value={formData.quantity}
          onChange={handleChange}
          min="1"
          required
          placeholder="Quantity"
        />
      </Form.Group>
      {/* Only show submit button if not hidden by prop */}
      {props.showSubmitButton !== false && (
        <Button type="submit" variant="primary">
          Add Product
        </Button>
      )}
    </Form>
  );
};

export default AddProductForm;